import { zodResolver } from '@hookform/resolvers/zod';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, ArrowLeft, ChevronRight, Hash, MessagesSquare, UserPlus } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, Path, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type {
  DiscordAutomatedMessageOptions,
  DiscordAutomatedMessages,
  DiscordAutomationOption,
  DiscordGuildSummary,
} from '@/Config/api/queryClient';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { Item, ItemActions, ItemContent, ItemGroup } from '@/Domain/components/Shared/Item/Item';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { cn } from '@/Domain/utils/cn';

const MESSAGE_MAX_LENGTH = 1500;
const MESSAGE_MAX_RENDERED_LENGTH = 2000;
// Discord server and channel names are the longest supported placeholders.
const PLACEHOLDER_MAX_LENGTH = 100;
const DELAY_OPTIONS = [0, 2, 5, 10, 30] as const;
const PLACEHOLDERS = [
  'display_name',
  'username',
  'user_handle',
  'user_mention',
  'user_id',
  'bot_name',
  'server_name',
  'channel_name',
] as const;
// Every placeholder that needs the person identified, so the ticket-channel hint covers them all.
const USER_PLACEHOLDERS: readonly string[] = [
  'display_name',
  'username',
  'user_handle',
  'user_mention',
  'user_id',
];
const MESSAGE_TEMPLATE_TOKEN = new RegExp(`\\{\\{|\\}\\}|\\{(${PLACEHOLDERS.join('|')})\\}`, 'g');
// Spellings other Discord bots use, so a rejected template can name the one that works here.
const PLACEHOLDER_ALIASES: Record<string, string> = {
  user: 'user_mention',
  mention: 'user_mention',
  'user.mention': 'user_mention',
  'user.id': 'user_id',
  userid: 'user_id',
  'user.displayname': 'display_name',
  'user.display_name': 'display_name',
  'user.nickname': 'display_name',
  'user.nick': 'display_name',
  displayname: 'display_name',
  nickname: 'display_name',
  'user.name': 'user_handle',
  'user.username': 'user_handle',
  handle: 'user_handle',
  server: 'server_name',
  'server.name': 'server_name',
  guild: 'server_name',
  guild_name: 'server_name',
  channel: 'channel_name',
  'channel.name': 'channel_name',
  bot: 'bot_name',
  'bot.name': 'bot_name',
  botname: 'bot_name',
};
// Radix Select rejects an empty item value, so "no category" travels under a sentinel.
const ANY_CATEGORY = '__any__';

const isMessageTemplateValid = (value: string): boolean =>
  !/[{}]/.test(value.replace(MESSAGE_TEMPLATE_TOKEN, ''));

/** Matched, not searched for: `{{username}}` is an escaped literal that asks for no name. */
const usesUserPlaceholder = (value: string): boolean =>
  [...value.matchAll(MESSAGE_TEMPLATE_TOKEN)].some(
    (match) => match[1] !== undefined && USER_PLACEHOLDERS.includes(match[1]),
  );

/** The supported placeholder a rejected one was probably reaching for, once the valid ones are gone. */
const suggestedPlaceholder = (value: string): string | undefined => {
  const unsupported = value.replace(MESSAGE_TEMPLATE_TOKEN, '').match(/\{([^{}]*)\}/);
  return unsupported ? PLACEHOLDER_ALIASES[unsupported[1].toLowerCase()] : undefined;
};

const getMaximumRenderedLength = (value: string): number => {
  let renderedLength = 0;
  let cursor = 0;
  for (const match of value.matchAll(MESSAGE_TEMPLATE_TOKEN)) {
    const index = match.index ?? cursor;
    renderedLength += index - cursor;
    renderedLength += match[1] ? PLACEHOLDER_MAX_LENGTH : 1;
    cursor = index + match[0].length;
  }
  return renderedLength + value.length - cursor;
};

export type DiscordAutomatedMessageRuleKey = 'member_joined' | 'thread_created' | 'channel_created';
export type DiscordAutomatedMessageSaveSource = 'toggle' | 'detail';

interface RuleConfig {
  key: DiscordAutomatedMessageRuleKey;
  icon: typeof UserPlus;
  optionsKey: keyof DiscordAutomatedMessageOptions;
  enabledName: Path<DiscordAutomatedMessages>;
  locationName: Path<DiscordAutomatedMessages>;
  messageName: Path<DiscordAutomatedMessages>;
  delayName: Path<DiscordAutomatedMessages>;
  testId: string;
  /** channel_created alone may run untargeted, meaning every new channel. */
  locationOptional?: boolean;
}

// Order is the display order: the most compelling rule first.
const RULES: RuleConfig[] = [
  {
    key: 'member_joined',
    icon: UserPlus,
    optionsKey: 'channels',
    enabledName: 'member_joined.enabled',
    locationName: 'member_joined.channel_id',
    messageName: 'member_joined.message',
    delayName: 'member_joined.delay_seconds',
    testId: 'discord-automation-member-joined',
  },
  {
    key: 'thread_created',
    icon: MessagesSquare,
    optionsKey: 'thread_parent_channels',
    enabledName: 'thread_created.enabled',
    locationName: 'thread_created.parent_channel_id',
    messageName: 'thread_created.message',
    delayName: 'thread_created.delay_seconds',
    testId: 'discord-automation-thread-created',
  },
  {
    key: 'channel_created',
    icon: Hash,
    optionsKey: 'categories',
    enabledName: 'channel_created.enabled',
    locationName: 'channel_created.category_id',
    messageName: 'channel_created.message',
    delayName: 'channel_created.delay_seconds',
    testId: 'discord-automation-channel-created',
    locationOptional: true,
  },
];

const tr = (rule: DiscordAutomatedMessageRuleKey, key: string) =>
  `integration:discord.oneclick.automated_messages.${rule}.${key}`;
const shared = (key: string) => `integration:discord.oneclick.automated_messages.${key}`;

interface PlaceholderChipsProps {
  onInsert: (token: string) => void;
  disabled?: boolean;
  testId: string;
}

/** AI Actions' badge uses primary-25/100/600, which this theme never defines. */
const PlaceholderChips = ({ onInsert, disabled, testId }: PlaceholderChipsProps) => {
  const { t } = useTranslation(['integration']);
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-xs text-muted-foreground">{t(shared('insert_placeholder'))}</span>
      {PLACEHOLDERS.map((placeholder) => {
        const token = `{${placeholder}}`;
        return (
          <button
            key={placeholder}
            type="button"
            disabled={disabled}
            onClick={() => onInsert(token)}
            aria-label={t(shared('insert_placeholder_aria'), { placeholder: token })}
            className={cn(
              'inline-flex cursor-pointer items-center rounded-md border border-primary/15 bg-primary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-primary transition-colors hover:bg-primary/20',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            data-testid={`${testId}-insert-${placeholder}`}
          >
            {token}
          </button>
        );
      })}
    </div>
  );
};

interface RuleDetailProps {
  rule: RuleConfig;
  control: Control<DiscordAutomatedMessages>;
  onLocationChange: () => void;
  options: DiscordAutomationOption[];
  locationError?: string;
  messageError?: string;
  disabled?: boolean;
  notice?: ReactNode;
}

const RuleDetail = ({
  rule,
  control,
  onLocationChange,
  options,
  locationError,
  messageError,
  disabled,
  notice,
}: RuleDetailProps) => {
  const { t } = useTranslation(['integration']);
  // From the field's own events: AutosizeTextarea's handle is not the DOM node its type claims.
  const messageEl = useRef<HTMLTextAreaElement | null>(null);
  const userHintId = `${rule.testId}-user-hint`;
  const delayHintId = `${rule.testId}-delay-hint`;
  const location = useWatch({ control, name: rule.locationName });

  return (
    <FieldGroup className="gap-4">
      <Controller
        control={control}
        name={rule.enabledName}
        render={({ field }) => (
          <IntegrationToggleField
            id={`${rule.testId}-enabled`}
            label={t(shared('enable_label'))}
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={disabled}
            data-testid={`${rule.testId}-enabled`}
          />
        )}
      />
      {notice}
      {/* Separator, not FieldSeparator: that one splits itself to seat a centred caption. */}
      <Separator />

      <Field data-invalid={Boolean(locationError)}>
        <FieldLabel htmlFor={`${rule.testId}-location`}>
          {t(tr(rule.key, 'location_label'))}
        </FieldLabel>
        <FieldDescription>{t(tr(rule.key, 'location_description'))}</FieldDescription>
        <Controller
          control={control}
          name={rule.locationName}
          render={({ field }) => (
            <Select
              value={String(field.value) || (rule.locationOptional ? ANY_CATEGORY : '')}
              onValueChange={(value) => {
                field.onChange(value === ANY_CATEGORY ? '' : value);
                // The form validates on submit, so a destination the user just fixed would keep
                // showing "the bot cannot post there" until they saved.
                onLocationChange();
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={`${rule.testId}-location`}
                aria-invalid={Boolean(locationError)}
                data-testid={`${rule.testId}-location`}
              >
                <SelectValue placeholder={t(tr(rule.key, 'location_placeholder'))} />
              </SelectTrigger>
              {/* No SelectGroup: it collapses the viewport to one child, killing its space-y. */}
              <SelectContent>
                {rule.locationOptional && (
                  <SelectItem value={ANY_CATEGORY}>{t(tr(rule.key, 'any_category'))}</SelectItem>
                )}
                {options.length
                  ? options.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                        {option.can_send === false && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t(shared('location_unwritable_option'))}
                          </span>
                        )}
                      </SelectItem>
                    ))
                  : !rule.locationOptional && (
                      <SelectItem value="unavailable" disabled>
                        {t(tr(rule.key, 'empty'))}
                      </SelectItem>
                    )}
              </SelectContent>
            </Select>
          )}
        />
        <FieldError>{locationError}</FieldError>
      </Field>

      <Field data-invalid={Boolean(messageError)}>
        <FieldLabel htmlFor={`${rule.testId}-message`}>{t(shared('message_label'))}</FieldLabel>
        <Controller
          control={control}
          name={rule.messageName}
          render={({ field }) => {
            // Only this rule infers the name; Discord reports it outright for the other two.
            const showUserHint =
              rule.key === 'channel_created' && usesUserPlaceholder(String(field.value));
            return (
              <>
                <AutosizeTextarea
                  id={`${rule.testId}-message`}
                  value={String(field.value)}
                  onChange={field.onChange}
                  onFocus={(event) => {
                    messageEl.current = event.currentTarget;
                  }}
                  onSelect={(event) => {
                    messageEl.current = event.currentTarget;
                  }}
                  minHeight={96}
                  maxHeight={180}
                  maxLength={MESSAGE_MAX_LENGTH}
                  disabled={disabled}
                  aria-invalid={Boolean(messageError)}
                  aria-describedby={showUserHint ? userHintId : undefined}
                  error={Boolean(messageError)}
                  className="px-4"
                  data-testid={`${rule.testId}-message`}
                />
                <PlaceholderChips
                  disabled={disabled}
                  testId={rule.testId}
                  onInsert={(token) => {
                    const element = messageEl.current;
                    const current = String(field.value);
                    if (!element) {
                      field.onChange(current + token);
                      return;
                    }
                    const start = element.selectionStart ?? current.length;
                    const end = element.selectionEnd ?? start;
                    field.onChange(current.slice(0, start) + token + current.slice(end));
                    requestAnimationFrame(() => {
                      element.focus();
                      const caret = start + token.length;
                      element.setSelectionRange(caret, caret);
                    });
                  }}
                />
                {showUserHint && (
                  <FieldDescription id={userHintId} data-testid={userHintId}>
                    {t(shared('user_inferred_description'))}
                  </FieldDescription>
                )}
              </>
            );
          }}
        />
        <FieldError>{messageError}</FieldError>
      </Field>

      <Field>
        <FieldLabel htmlFor={`${rule.testId}-delay`}>{t(shared('delay_label'))}</FieldLabel>
        <FieldDescription>{t(shared('delay_description'))}</FieldDescription>
        <Controller
          control={control}
          name={rule.delayName}
          render={({ field }) => {
            // A ticket bot writes the requester's permissions after it creates the channel, so
            // with no delay there is nobody to name yet and the greeting reads "there".
            const showTicketDelayHint =
              rule.key === 'channel_created' && Boolean(location) && Number(field.value) === 0;
            return (
              <>
                <Select
                  value={String(field.value)}
                  onValueChange={(value) => field.onChange(Number(value))}
                  disabled={disabled}
                >
                  <SelectTrigger
                    id={`${rule.testId}-delay`}
                    aria-describedby={showTicketDelayHint ? delayHintId : undefined}
                    data-testid={`${rule.testId}-delay`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DELAY_OPTIONS.map((seconds) => (
                      <SelectItem key={seconds} value={String(seconds)}>
                        {seconds === 0
                          ? t(shared('delay_none'))
                          : t(shared('delay_seconds'), { seconds })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showTicketDelayHint && (
                  <FieldDescription id={delayHintId} data-testid={delayHintId}>
                    {t(shared('delay_ticket_description'))}
                  </FieldDescription>
                )}
              </>
            );
          }}
        />
      </Field>
    </FieldGroup>
  );
};

interface RuleRowProps {
  rule: RuleConfig;
  control: Control<DiscordAutomatedMessages>;
  summary: string;
  onOpen: () => void;
  onToggle: (enabled: boolean, revert: () => void) => void;
  disabled?: boolean;
  saving?: boolean;
}

const RuleRow = ({ rule, control, summary, onOpen, onToggle, disabled, saving }: RuleRowProps) => {
  const { t } = useTranslation(['integration']);
  const title = t(tr(rule.key, 'title'));
  const Icon = rule.icon;
  return (
    <Item
      variant="outline"
      size="sm"
      className="gap-3 hover:bg-accent"
      data-testid={`${rule.testId}-row`}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-label={t(shared('configure_rule'), { rule: title })}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left disabled:cursor-not-allowed"
        data-testid={`${rule.testId}-open`}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <ItemContent className="min-w-0 gap-0.5">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="line-clamp-1 text-xs text-muted-foreground">{summary}</p>
        </ItemContent>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover/item:translate-x-0.5 group-hover/item:text-foreground" />
      </button>
      {/* The chevron sat flush against the switch and read as if it pointed at it. */}
      <div className="h-8 w-px shrink-0 bg-border" />
      <ItemActions className="relative">
        {saving ? (
          <Spinner
            className="absolute right-full top-1/2 mr-2 -translate-y-1/2 size-3.5 text-foreground-subtle"
            data-testid={`${rule.testId}-saving`}
          />
        ) : null}
        <Controller
          control={control}
          name={rule.enabledName}
          render={({ field }) => (
            <Switch
              checked={Boolean(field.value)}
              onCheckedChange={(next) => {
                field.onChange(next);
                onToggle(next, () => field.onChange(!next));
              }}
              disabled={disabled}
              aria-label={t(shared('enable_rule'), { rule: title })}
              data-testid={`${rule.testId}-toggle`}
            />
          )}
        />
      </ItemActions>
    </Item>
  );
};

interface DiscordAutomatedMessagesDialogProps {
  open: boolean;
  guild: DiscordGuildSummary | null;
  options?: DiscordAutomatedMessageOptions;
  optionsLoading: boolean;
  optionsError: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onRetryOptions: () => void;
  noticeForRule?: (rule: DiscordAutomatedMessageRuleKey) => ReactNode;
  onSave: (
    guildId: string,
    messages: DiscordAutomatedMessages,
    meta: {
      rule: DiscordAutomatedMessageRuleKey;
      source: DiscordAutomatedMessageSaveSource;
    },
  ) => Promise<boolean>;
}

export const DiscordAutomatedMessagesDialog = ({
  open,
  guild,
  options,
  optionsLoading,
  optionsError,
  saving,
  onOpenChange,
  onRetryOptions,
  noticeForRule,
  onSave,
}: DiscordAutomatedMessagesDialogProps) => {
  const { t } = useTranslation(['integration']);
  const [openRule, setOpenRule] = useState<DiscordAutomatedMessageRuleKey | null>(null);
  const [direction, setDirection] = useState(1);
  const [savingRule, setSavingRule] = useState<DiscordAutomatedMessageRuleKey | null>(null);
  const reduceMotion = useReducedMotion();
  // Measured, not framer `layout`: that animates size by scaling and stretches the text.
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [bodyHeight, setBodyHeight] = useState<number>();

  useEffect(() => {
    if (!bodyEl) return;
    const observer = new ResizeObserver(([entry]) =>
      setBodyHeight(entry.borderBoxSize[0].blockSize),
    );
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  const schema = useMemo(() => {
    const message = z
      .string()
      .trim()
      .min(1, t(shared('message_required')))
      .max(MESSAGE_MAX_LENGTH)
      .superRefine((value, context) => {
        if (isMessageTemplateValid(value)) return;
        const suggestion = suggestedPlaceholder(value);
        context.addIssue({
          code: 'custom',
          message: suggestion
            ? t(shared('placeholder_suggestion'), { suggestion: `{${suggestion}}` })
            : t(shared('placeholder_invalid')),
        });
      })
      .refine(
        (value) => getMaximumRenderedLength(value) <= MESSAGE_MAX_RENDERED_LENGTH,
        t(shared('message_too_long')),
      );
    const rule = {
      enabled: z.boolean(),
      delay_seconds: z
        .number()
        .int()
        .refine((value) => DELAY_OPTIONS.includes(value as (typeof DELAY_OPTIONS)[number])),
      message,
    };
    return z
      .object({
        channel_created: z.object({ ...rule, category_id: z.string() }),
        member_joined: z.object({ ...rule, channel_id: z.string() }),
        thread_created: z.object({ ...rule, parent_channel_id: z.string() }),
      })
      .superRefine((value, context) => {
        const required = t(shared('location_required'));
        const unavailable = t(shared('location_unavailable'));
        const unwritable = (permission: string) => t(shared('location_unwritable'), { permission });
        const locationIssue = (
          available: DiscordAutomationOption[] | undefined,
          id: string,
          // Discord's own label for the permission, left in English as our docs do.
          permission = 'Send Messages',
        ): string | undefined => {
          const option = available?.find((candidate) => candidate.id === id);
          if (!option) return unavailable;
          return option.can_send === false ? unwritable(permission) : undefined;
        };
        // An empty category is the "every new channel" case, so only a set one is checked.
        const categoryIssue =
          value.channel_created.enabled && value.channel_created.category_id
            ? locationIssue(options?.categories, value.channel_created.category_id)
            : undefined;
        if (categoryIssue) {
          context.addIssue({
            code: 'custom',
            message: categoryIssue,
            path: ['channel_created', 'category_id'],
          });
        }
        if (value.member_joined.enabled && !value.member_joined.channel_id) {
          context.addIssue({
            code: 'custom',
            message: required,
            path: ['member_joined', 'channel_id'],
          });
        } else if (value.member_joined.enabled) {
          const issue = locationIssue(options?.channels, value.member_joined.channel_id);
          if (issue) {
            context.addIssue({
              code: 'custom',
              message: issue,
              path: ['member_joined', 'channel_id'],
            });
          }
        }
        if (value.thread_created.enabled && !value.thread_created.parent_channel_id) {
          context.addIssue({
            code: 'custom',
            message: required,
            path: ['thread_created', 'parent_channel_id'],
          });
        } else if (value.thread_created.enabled) {
          const issue = locationIssue(
            options?.thread_parent_channels,
            value.thread_created.parent_channel_id,
            'Send Messages in Threads',
          );
          if (issue) {
            context.addIssue({
              code: 'custom',
              message: issue,
              path: ['thread_created', 'parent_channel_id'],
            });
          }
        }
      });
  }, [options, t]);

  const form = useForm<DiscordAutomatedMessages>({
    resolver: zodResolver(schema),
    defaultValues: guild?.automated_messages,
  });
  const values = useWatch({ control: form.control });

  useEffect(() => {
    if (open && guild?.automated_messages) form.reset(guild.automated_messages);
  }, [open, guild, form]);

  useEffect(() => {
    if (!open) {
      setOpenRule(null);
      setDirection(1);
    }
  }, [open]);

  const activeRule = RULES.find(({ key }) => key === openRule) ?? null;

  const summaryFor = (rule: RuleConfig): string => {
    const rows = values?.[rule.key];
    if (!rows?.enabled) return t(tr(rule.key, 'description'));
    const delay = rows.delay_seconds
      ? t(shared('delay_seconds'), { seconds: rows.delay_seconds })
      : t(shared('delay_none'));
    const locationId =
      rule.key === 'member_joined'
        ? values?.member_joined?.channel_id
        : rule.key === 'thread_created'
          ? values?.thread_created?.parent_channel_id
          : values?.channel_created?.category_id;
    const location = locationId
      ? (options?.[rule.optionsKey].find(({ id }) => id === locationId)?.name ??
        t(shared('location_unavailable')))
      : rule.locationOptional
        ? t(tr(rule.key, 'summary_any'))
        : t(tr(rule.key, 'location_placeholder'));
    return t(shared('summary_on'), { location, delay });
  };

  // Only a targeted rule needs the resource list, so untargeted stays saveable in an outage.
  const ruleNeedsOptions = (rule: RuleConfig | null): boolean => {
    if (!rule) return false;
    const current = values?.[rule.key];
    if (!current?.enabled) return false;
    return rule.locationOptional ? Boolean(values?.channel_created?.category_id) : true;
  };

  const errors = form.formState.errors;
  const errorFor = (rule: RuleConfig) => {
    const ruleErrors = errors[rule.key];
    const location =
      rule.key === 'member_joined'
        ? errors.member_joined?.channel_id
        : rule.key === 'thread_created'
          ? errors.thread_created?.parent_channel_id
          : errors.channel_created?.category_id;
    return { location: location?.message, message: ruleErrors?.message?.message };
  };

  const openDetail = (key: DiscordAutomatedMessageRuleKey) => {
    setDirection(1);
    setOpenRule(key);
    // A rule enabled months ago can have stopped working since. Say so on open, not on save.
    if (values?.[key]?.enabled) void form.trigger(key);
  };

  /** Back, Cancel, the backdrop and Escape all mean the same thing: drop this rule's edits. */
  const leaveDetail = () => {
    if (activeRule && guild?.automated_messages) {
      form.reset({
        ...form.getValues(),
        [activeRule.key]: guild.automated_messages[activeRule.key],
      });
    }
    setDirection(-1);
    setOpenRule(null);
  };

  const persist = async (
    rule: RuleConfig,
    source: DiscordAutomatedMessageSaveSource,
  ): Promise<boolean> => {
    if (!guild) return false;
    setSavingRule(rule.key);
    const saved = await onSave(guild.guild_id, form.getValues(), { rule: rule.key, source });
    setSavingRule(null);
    return saved;
  };

  const toggleRule = async (rule: RuleConfig, enabled: boolean, revert: () => void) => {
    // Switching on a rule with nowhere to run is the common fresh-server case: open it.
    if (enabled && !(await form.trigger(rule.key))) {
      openDetail(rule.key);
      return;
    }
    form.clearErrors(rule.key);
    if (!(await persist(rule, 'toggle'))) revert();
  };

  const saveDetail = async (rule: RuleConfig) => {
    // Scoped to this rule; the server merges it, so a stale sibling cannot block the edit.
    if (!(await form.trigger(rule.key))) return;
    if (await persist(rule, 'detail')) {
      setDirection(-1);
      setOpenRule(null);
    }
  };

  const dismiss = () => {
    if (saving) return;
    if (activeRule) leaveDetail();
    else onOpenChange(false);
  };

  return (
    // Locked while saving: reopening this guild mid-flight would let the old save close the new dialog.
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        aria-describedby="discord-automated-messages-description"
        onEscapeKeyDown={(event) => {
          if (saving || activeRule) {
            event.preventDefault();
            dismiss();
          }
        }}
        // This layer spans the Sheet so Radix never sees an outside-press; mousedown survives drags.
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) dismiss();
        }}
        data-testid="discord-automated-messages-backdrop"
      >
        <form
          className="flex max-h-[calc(100%-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-card shadow-lg"
          onSubmit={(event) => {
            event.preventDefault();
            if (activeRule) void saveDetail(activeRule);
          }}
          data-testid="discord-automated-messages-dialog"
        >
          <DialogHeader className="flex-row items-start justify-between gap-3 border-b border-border p-6">
            {activeRule && (
              <IconButton
                type="button"
                variant="secondary"
                onClick={leaveDetail}
                disabled={saving}
                aria-label={t(shared('back'))}
                data-testid="discord-automated-messages-back"
              >
                <ArrowLeft />
              </IconButton>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <DialogTitle>
                {activeRule
                  ? t(tr(activeRule.key, 'title'))
                  : t(shared('title'), { server: guild?.guild_name ?? '' })}
              </DialogTitle>
              <DialogDescription id="discord-automated-messages-description">
                {activeRule ? t(tr(activeRule.key, 'description')) : t(shared('description'))}
              </DialogDescription>
            </div>
            <IconButton
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              aria-label={t(shared('close'))}
              data-testid="discord-automated-messages-close"
            >
              <CloseIcon />
            </IconButton>
          </DialogHeader>

          <div className="overflow-y-auto">
            <motion.div
              className="overflow-hidden"
              initial={false}
              animate={{ height: bodyHeight ?? 'auto' }}
              transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.4, 0, 0.2, 1] }}
            >
              <div ref={setBodyEl} className="p-6">
                {optionsLoading ? (
                  <div
                    className="flex justify-center py-12"
                    data-testid="discord-automation-loading"
                  >
                    <Spinner />
                  </div>
                ) : (
                  <FieldGroup>
                    {(optionsError || !options) && (
                      <Alert variant="destructive" data-testid="discord-automation-options-error">
                        <AlertCircle data-icon="inline-start" />
                        <AlertDescription className="flex flex-col items-start gap-3">
                          <p className="m-0">{t(shared('options_error'))}</p>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={onRetryOptions}
                          >
                            {t('integration:discord.oneclick.retry')}
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    {/* One phase: an exit animation would play out, resize, then play in. */}
                    <motion.div
                      key={openRule ?? 'list'}
                      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: direction * 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
                    >
                      {activeRule ? (
                        <RuleDetail
                          rule={activeRule}
                          control={form.control}
                          options={options?.[activeRule.optionsKey] ?? []}
                          locationError={errorFor(activeRule).location}
                          onLocationChange={() => void form.trigger(activeRule.key)}
                          messageError={errorFor(activeRule).message}
                          disabled={saving}
                          notice={noticeForRule?.(activeRule.key)}
                        />
                      ) : (
                        <ItemGroup className="gap-2">
                          {RULES.map((rule) => (
                            <RuleRow
                              key={rule.key}
                              rule={rule}
                              control={form.control}
                              summary={summaryFor(rule)}
                              onOpen={() => openDetail(rule.key)}
                              onToggle={(enabled, revert) => void toggleRule(rule, enabled, revert)}
                              disabled={saving}
                              saving={savingRule === rule.key && saving}
                            />
                          ))}
                        </ItemGroup>
                      )}
                    </motion.div>
                  </FieldGroup>
                )}
              </div>
            </motion.div>
          </div>

          {/* Collapsed, not unmounted: mounting it popped the card 70px in one frame. */}
          <motion.div
            className="shrink-0 overflow-hidden"
            initial={false}
            animate={{ height: activeRule ? 'auto' : 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.4, 0, 0.2, 1] }}
            aria-hidden={!activeRule}
          >
            <DialogFooter className="border-t border-border p-6">
              <Button
                type="button"
                variant="secondary"
                onClick={leaveDetail}
                disabled={saving || !activeRule}
                tabIndex={activeRule ? undefined : -1}
                data-testid="discord-automated-messages-cancel"
              >
                {t(shared('cancel'))}
              </Button>
              <Button
                type="submit"
                variant="dark"
                disabled={
                  saving ||
                  optionsLoading ||
                  !activeRule ||
                  ((optionsError || !options) && ruleNeedsOptions(activeRule))
                }
                tabIndex={activeRule ? undefined : -1}
                data-testid="discord-automated-messages-save"
              >
                {saving ? <Spinner data-icon="inline-start" /> : null}
                {t(shared('save'))}
              </Button>
            </DialogFooter>
          </motion.div>
        </form>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};

export default DiscordAutomatedMessagesDialog;
